import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function GoogleAccountCompletion({ onSubmit, onClose, userData }) {
  const { t } = useTranslation('auth');
  const [phone, setPhone] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!phone.trim()) {
      newErrors.phone = t('phone_required');
    } else {
      const bgPattern = /^08\d{8}$/;
      const intlPattern = /^00\d{8,14}$/;
      if (!bgPattern.test(phone) && !intlPattern.test(phone)) {
        newErrors.phone = t('phone_invalid_bg');
      }
    }

    if (!consentGiven) {
      newErrors.consent = t('consent_required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setErrors({});
    try {
      await onSubmit(phone);
    } catch (err) {
      setErrors({ submit: err.message || t('error', { ns: 'common' }) });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (value) => {
    setPhone(value);
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 animate-fadeIn">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          {t('complete_registration_title')}
        </h2>
        <p className="text-gray-600 mb-6">
          {t('complete_registration_subtitle', { name: userData?.display_name || userData?.email })}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              {t('phone_label')} <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => handleChange(e.target.value)}
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.phone ? 'border-red-500' : 'border-gray-300'
              } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
              placeholder="08XXXXXXXX"
              disabled={loading}
            />
            {errors.phone && (
              <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
            )}
            <p className="text-gray-500 text-xs mt-2">
              {t('phone_hint_bg')}<br />
              {t('phone_hint_intl')}
            </p>
          </div>

          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-600 text-sm">{errors.submit}</p>
            </div>
          )}

          <label className="flex items-start gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={consentGiven}
              onChange={(e) => {
                setConsentGiven(e.target.checked);
                if (errors.consent) setErrors(prev => ({ ...prev, consent: '' }));
              }}
              className="mt-1 w-4 h-4 rounded accent-purple-700"
              disabled={loading}
            />
            <span>
              {t('consent_prefix')}{' '}
              <a href="/privacy" target="_blank" className="text-purple-700 underline">{t('privacy_policy')}</a>
              {' '}{t('consent_and')}{' '}
              <a href="/terms" target="_blank" className="text-purple-700 underline">{t('terms_of_service')}</a>.
            </span>
          </label>
          {errors.consent && <p className="text-red-500 text-xs mt-1">{errors.consent}</p>}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
            >
              {t('cancel', { ns: 'common' })}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 bg-purple-700 text-white rounded-lg hover:bg-purple-800 transition disabled:opacity-50"
            >
              {loading ? t('saving', { ns: 'common' }) : t('complete_registration_btn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}